/** Detalle de requerimiento: lectura + editar + acciones + adjuntos + historial. */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { SectionCard } from '@/components/SectionCard';
import { StatusHistoryCard } from '@/components/StatusHistoryCard';
import { Badge, StatusBadge } from '@/components/StatusBadge';
import { RequirementCompositionCard } from '@/components/RequirementCompositionCard';
import { FileCapture } from '@/components/FileCapture';
import { ServerActionRunner } from '@/components/ServerActionRunner';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import {
  useMobileRequirementAttachmentsQuery,
  useMobileRequirementContextQuery,
  useMobileRequirementObservationsQuery,
  useMobileRequirementQuery,
  useRequirementActionMutation,
  useUploadRequirementAttachmentsMutation,
} from '@/hooks/useMobileRequirements';
import { useMobileWorkflowActions } from '@/hooks/useMobileWorkflowActions';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { formatRelativeDate } from '@/utils/format';
import { hapticSuccess } from '@/utils/haptic';

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function typeLabel(type: string | undefined): string {
  if (type === 'project') return 'Proyecto';
  return 'Estandar';
}

export function RequirementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useMobileAuth();
  const requirementId = id ?? null;
  const requirementQuery = useMobileRequirementQuery(requirementId);
  const contextQuery = useMobileRequirementContextQuery(requirementId);
  const attachmentsQuery = useMobileRequirementAttachmentsQuery(requirementId);
  const observationsQuery = useMobileRequirementObservationsQuery(requirementId);
  const uploadMutation = useUploadRequirementAttachmentsMutation();
  const actionMutation = useRequirementActionMutation(requirementId ?? '');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const requirement = requirementQuery.data;
  const contextActions = contextQuery.data?.actions ?? [];
  const documentRevision =
    contextQuery.data?.document_revision ?? requirementQuery.data?.workflow_revision;
  const activeInstance = (contextQuery.data?.latest_instances ?? []).find(
    (instance) => instance.status === 'pending' || instance.status === 'blocked',
  );
  const workflowActions = useMobileWorkflowActions({
    entityCode: 'customer_requirement',
    entityId: requirementId ?? '',
    actions: contextActions,
    documentRevision,
    activeInstanceId: activeInstance?.id,
    instanceRevision: activeInstance?.revision,
    onDomainCommand: (actionCode, body, revision) =>
      actionMutation.mutateAsync({ action: actionCode, body, revision }),
  });

  async function handleFiles(files: File[]) {
    if (!requirementId || files.length === 0) return;
    setUploadError(null);
    try {
      await uploadMutation.mutateAsync({ requirementId, files });
      hapticSuccess();
      toast.success(files.length === 1 ? 'Adjunto subido correctamente' : 'Adjuntos subidos correctamente');
    } catch (error) {
      setUploadError(extractErrorMessage(error));
    }
  }

  function handleActionSuccess(actionCode: string, result: unknown) {
    hapticSuccess();
    if (actionCode === 'send_to_budget') {
      const budgetId =
        result !== null && typeof result === 'object'
          ? String((result as { budget_id?: unknown }).budget_id ?? '')
          : '';
      toast.success('Presupuesto creado correctamente');
      if (budgetId) {
        navigate(`/m/budgets/${budgetId}`);
        return;
      }
    } else {
      toast.success('Acción ejecutada correctamente');
    }
    void requirementQuery.refetch();
    void contextQuery.refetch();
  }

  if (requirementQuery.isPending) {
    return (
      <MobileLayout title="Requerimiento" showBack>
        <CardListSkeleton count={3} />
      </MobileLayout>
    );
  }

  if (requirementQuery.isError || !requirement) {
    return (
      <MobileLayout title="Requerimiento" showBack>
        <EmptyState
          icon="error"
          title="No se pudo cargar"
          description="Verifica tu conexión e intenta de nuevo."
          actionLabel="Reintentar"
          onAction={() => void requirementQuery.refetch()}
        />
      </MobileLayout>
    );
  }

  const attachments = attachmentsQuery.data ?? [];
  const observations = observationsQuery.data ?? [];
  const openObservations = observations.filter((observation) => observation.status === 'open');
  const hasActiveRequest =
    requirement.workflow_summary?.has_active_instance === true ||
    (contextQuery.data?.latest_instances ?? []).some(
      (instance) => instance.status === 'pending' || instance.status === 'blocked',
    );
  const editableByStatus = ['draft', 'technical_review', 'observed', 'in_design'].includes(
    requirement.status,
  );
  const editBlockReason = hasActiveRequest
    ? 'Hay una solicitud activa sobre este requerimiento.'
    : !editableByStatus
      ? 'El requerimiento ya no admite edición.'
      : null;
  const canEditRequirement = hasPermission('customer_requirements.update') && !editBlockReason;
  const actionsForUi = contextActions.map((action) => {
    const code = action.action_code ?? action.code;
    if (code === 'observe' && openObservations.length === 0) {
      return {
        ...action,
        enabled: false,
        disabled_reason: 'Registra al menos una observación abierta antes de observar.',
      };
    }
    return action;
  });

  return (
    <MobileLayout title={requirement.code || 'Requerimiento'} subtitle={requirement.name} showBack>
      <div className="space-y-4">
        <SectionCard
          title="Datos generales"
          action={<StatusBadge status={requirement.status} size="sm" />}
        >
          <dl className="divide-y divide-border-light text-sm">
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Codigo</dt>
              <dd title={requirement.code} className="min-w-0 flex-1 truncate text-right font-semibold text-text-primary">
                {requirement.code || '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Cliente</dt>
              <dd className="min-w-0 flex-1 break-words text-right font-medium text-text-primary">
                {requirement.client?.legal_name ?? '—'}
              </dd>
            </div>
            {requirement.client?.tax_id && (
              <div className="flex items-center justify-between gap-3 py-2">
                <dt className="shrink-0 text-text-tertiary">RUC</dt>
                <dd title={requirement.client.tax_id} className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                  {requirement.client.tax_id}
                </dd>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Tipo</dt>
              <dd className="min-w-0 flex-1 truncate text-right text-text-secondary">
                {typeLabel(requirement.type)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Origen</dt>
              <dd className="min-w-0 flex-1 text-right">
                {requirement.source === 'quote_generator' ? (
                  <Badge variant="info" size="sm">Cotizador</Badge>
                ) : (
                  <span className="text-text-secondary">Real</span>
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Solicitado</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatDate(requirement.requested_date)}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-2">
              <dt className="shrink-0 text-text-tertiary">Requerido</dt>
              <dd className="min-w-0 flex-1 truncate text-right tabular-nums text-text-secondary">
                {formatDate(requirement.required_date)} · {formatRelativeDate(requirement.required_date)}
              </dd>
            </div>
          </dl>
          {requirement.description && (
            <p className="mt-3 break-words border-t border-border-light pt-3 text-sm text-text-secondary">
              {requirement.description}
            </p>
          )}
          {hasPermission('customer_requirements.update') && (
            <div className="mt-4">
              <MobileButton
                variant="secondary"
                size="md"
                fullWidth
                disabled={!canEditRequirement}
                icon={<MobileIcon name="edit_note" className="h-5 w-5" />}
                onClick={() => navigate(`/m/requirements/${requirement.id}/edit`)}
              >
                Editar
              </MobileButton>
              {!canEditRequirement && editBlockReason && (
                <p className="mt-1 break-words px-1 text-xs text-text-tertiary">
                  {editBlockReason}
                </p>
              )}
            </div>
          )}
        </SectionCard>

        {requirement.quote_snapshot && (
          <RequirementCompositionCard snapshot={requirement.quote_snapshot} />
        )}

        {contextActions.length > 0 && (
          <SectionCard
            title="Acciones"
            subtitle="El servidor habilito estas acciones para este requerimiento."
          >
            <ServerActionRunner
              actions={actionsForUi}
              documentRevision={documentRevision}
              onExecute={workflowActions.execute}
              onSuccess={handleActionSuccess}
              onConflictReload={() => {
                void requirementQuery.refetch();
                void contextQuery.refetch();
              }}
            />
          </SectionCard>
        )}

        <SectionCard title={`Adjuntos (${attachments.length})`}>
          {attachmentsQuery.isPending && <CardListSkeleton count={2} />}
          {attachmentsQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudieron cargar los adjuntos"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => void attachmentsQuery.refetch()}
            />
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length === 0 && (
            <div className="mb-3 rounded-xl bg-bg-tertiary p-4 text-center">
              <p className="truncate text-sm font-semibold text-text-primary">Sin adjuntos</p>
              <p className="text-sm text-text-secondary">Agrega fotos o archivos del pedido.</p>
            </div>
          )}
          {!attachmentsQuery.isPending && !attachmentsQuery.isError && attachments.length > 0 && (
            <ul className="mb-3 space-y-2">
              {attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="flex min-h-[52px] items-center gap-2 rounded-xl bg-bg-tertiary px-4 py-2"
                >
                  <MobileIcon name="folder_open" className="h-5 w-5 shrink-0 text-text-tertiary" />
                  <span className="min-w-0 flex-1">
                    <span title={attachment.file_name} className="block truncate text-sm font-semibold text-text-primary">
                      {attachment.file_name}
                    </span>
                    {attachment.description && (
                      <span className="block break-words text-xs text-text-secondary line-clamp-2">
                        {attachment.description}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <FileCapture onFiles={(files) => void handleFiles(files)} disabled={uploadMutation.isPending} />
          {uploadMutation.isPending && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-info-light px-4 py-3">
              <MobileIcon name="sync" className="h-5 w-5 shrink-0 animate-spin text-info-dark" />
              <p className="min-w-0 flex-1 truncate text-sm text-info-dark">Subiendo adjuntos...</p>
            </div>
          )}
          {uploadError && (
            <div className="mt-2 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
              <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
              <p className="min-w-0 flex-1 break-words text-sm text-error">{uploadError}</p>
            </div>
          )}
        </SectionCard>

        <StatusHistoryCard
          entityType="customer_requirement"
          entityId={requirementId}
          enabled={hasPermission('customer_requirements.view')}
        />
      </div>
    </MobileLayout>
  );
}
